import './NewFeatureBadge.css';

import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { DuplicationTooltip } from '../../../orthanc_duplicata/ui/DuplicationTooltip';
import { isCurrentFeatureSeries } from '../../Utils/appVersion';

/**
 * Marks a feature as new for the length of a feature series. It shows while the
 * running release shares the major.minor of the version the feature shipped in,
 * so patch releases keep it and the next minor drops it. No state, no storage,
 * nothing to clean up.
 *
 * Reuse it by declaring the release a feature ships in:
 *   <NewFeatureBadge newInVersion="4.4.0" />
 */
const NewFeatureBadge = ({ newInVersion, label, variant }) => {
  const [t] = useTranslation('Common');

  if (!isCurrentFeatureSeries(newInVersion)) {
    return null;
  }

  const badge = (
    <span className={`newFeatureBadge newFeatureBadge--${variant}`}>
      {label || t('New')}
    </span>
  );

  if (variant !== 'corner') {
    return badge;
  }

  return (
    <>
      {badge}
      <DuplicationTooltip>{t('New')}</DuplicationTooltip>
    </>
  );
};

NewFeatureBadge.propTypes = {
  /** The release this feature shipped in, ie. '4.4.0' */
  newInVersion: PropTypes.string.isRequired,
  /** Overrides the translated default, which the caller must translate itself */
  label: PropTypes.string,
  /**
   * `inline` is a labelled pill sitting in the flow. `corner` is a one glyph
   * disc pinned to the top right of the closest positioned ancestor, for which
   * the caller is expected to pass a short `label`.
   */
  variant: PropTypes.oneOf(['inline', 'corner']),
};

NewFeatureBadge.defaultProps = {
  variant: 'inline',
};

export { NewFeatureBadge };
